import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Search, MapPinned, Users, BookOpen, LibraryBig,
  GraduationCap, Tag, DollarSign, Wallet, MessageCircle, BarChart3,
  Settings, Sparkles, PencilRuler, LifeBuoy, ShieldCheck, ClipboardCheck,
  Layers, Wrench, Palette, FolderPlus, Upload,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Atlas left rail. Renders inside AtlasLayout under the AtlasHeader.
 * Nav order + labels + icons per Phase 1 brief. Admin sub-section
 * only renders when `isAdmin` is true.
 *
 * Msg 388 · Nav labeling pass — items whose route still renders the
 * `AtlasComingSoonPage` scaffold now carry `pending: true` and show a
 * small "SOON" pill so advisors don't waste clicks hunting for features
 * that aren't live yet. `Search` is treated as active for the whole
 * search-and-shortlist flow (/atlas/search, /atlas/cards[/…],
 * /atlas/compare) since compare is a child step of Search.
 */

const NAV_TOP = [
  { to: "/atlas/dashboard",      label: "Dashboard",       icon: LayoutDashboard },
  { to: "/atlas/search",         label: "Search",          icon: Search,
    matchPrefixes: ["/atlas/search"] },
  { to: "/atlas/topics",         label: "Topics",          icon: BookOpen,
    matchPrefixes: ["/atlas/topics"] },
  { to: "/atlas/trips",          label: "Trips",           icon: MapPinned,     pending: true },
  { to: "/atlas/clients",        label: "Clients",         icon: Users,         pending: false },
  { to: "/atlas/playbook",       label: "Playbook",        icon: LibraryBig,    pending: true },
  { to: "/atlas/training",       label: "Training",        icon: GraduationCap, pending: true },
  { to: "/atlas/promotions",     label: "Promotions",      icon: Tag,           pending: true, badge: "NEW" },
  { to: "/atlas/commissions",    label: "Commissions",     icon: DollarSign,    pending: true },
  { to: "/atlas/rewards-wallet", label: "Rewards Wallet",  icon: Wallet },
  { to: "/atlas/messages",       label: "Messages",        icon: MessageCircle, pending: true },
  { to: "/atlas/reports",        label: "Reports",         icon: BarChart3,     pending: true },
  { to: "/atlas/settings",       label: "Settings",        icon: Settings },
];

const NAV_TOOLS = [
  { to: "/atlas/ai-assistant",  label: "Atlas AI Assistant", icon: Sparkles,   pending: true },
];

const baseCls = "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-hand tb-tap transition-colors ";
const activeCls   = "bg-tb-navy text-tb-gold-bright";
const inactiveCls = "text-tb-navy/70 hover:bg-tb-navy/5 hover:text-tb-navy";

function slug(label) {
  return label.toLowerCase().replace(/\s+/g, "-");
}

function ItemPill({ pending, badge, isActive }) {
  if (pending) {
    return (
      <span
        data-testid="atlas-nav-pending-pill"
        className={"ml-auto text-[9px] font-hand tracking-widest uppercase px-1.5 py-0.5 rounded-full border " +
          (isActive
            ? "bg-tb-gold-bright/25 border-tb-gold-bright/50 text-tb-gold-bright"
            : "bg-tb-navy/5 border-tb-navy/25 text-tb-navy/60")}
        title="Coming soon"
      >
        Soon
      </span>
    );
  }
  if (badge) {
    return (
      <span className="ml-auto text-[9px] font-hand tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-tb-gold/20 border border-tb-gold text-tb-navy">
        {badge}
      </span>
    );
  }
  return null;
}

/**
 * Msg 571 · Phase I.2 — Templates admin nav link. Owns its own tiny
 * fetch of the templates list so the heat-map chip is always fresh.
 * Refetches on every route change (cheap: single GET, admin-only).
 */
function TemplatesNavLink({ handleNav }) {
  const { pathname } = useLocation();
  const isActive = pathname === "/atlas/admin/templates"
    || pathname.startsWith("/atlas/admin/templates/")
    || pathname === "/atlas/admin/template-parent-map";
  const [stats, setStats] = React.useState({
    total: null, totalTopics: 0, unused: 0,
  });
  React.useEffect(() => {
    let cancelled = false;
    api.get("/atlas/admin/templates").then((r) => {
      if (cancelled) return;
      const rows = r.data?.templates || [];
      setStats({
        total: rows.length,
        totalTopics: rows.reduce((n, t) => n + (t.used_by_count || 0), 0),
        unused: rows.filter((t) => (t.used_by_count || 0) === 0).length,
      });
    }).catch(() => { /* silent — chip just stays hidden */ });
    return () => { cancelled = true; };
  }, [pathname]);
  const cls = baseCls + (isActive ? activeCls : inactiveCls);
  return (
    <NavLink
      to="/atlas/admin/templates"
      onClick={handleNav}
      className={cls}
      data-testid="atlas-nav-admin-templates"
    >
      <Layers className="w-4 h-4" strokeWidth={2} />
      <span className="flex-1">Templates</span>
      {stats.total !== null && (
        <span
          data-testid="atlas-nav-templates-heatmap"
          title={`${stats.total} templates · ${stats.totalTopics} topics using them${stats.unused ? ` · ${stats.unused} unused` : ""}`}
          className={"ml-auto text-[9px] font-hand tracking-widest uppercase px-1.5 py-0.5 rounded-full border " +
            (isActive
              ? "bg-tb-gold-bright/25 border-tb-gold-bright/50 text-tb-gold-bright"
              : "bg-tb-navy/5 border-tb-navy/20 text-tb-navy/70")}
        >
          {stats.total}·{stats.totalTopics}{stats.unused > 0 ? `·${stats.unused}!` : ""}
        </span>
      )}
    </NavLink>
  );
}

export default function AtlasNav({ isAdmin = false, onNavigate }) {
  const { pathname } = useLocation();

  const isActive = (item) => {
    if (item.matchPrefixes) {
      return item.matchPrefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));
    }
    return pathname === item.to || pathname.startsWith(item.to + "/");
  };

  // Msg 555 — thread the mobile-drawer close callback into every NavLink
  // so tapping a nav item inside the drawer closes it after navigating.
  const handleNav = () => { if (typeof onNavigate === "function") onNavigate(); };  return (
    <nav
      className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5"
      data-testid="atlas-nav"
    >
      {NAV_TOP.map((item) => {
        const { to, label, icon: Icon, badge, pending } = item;
        const active = isActive(item);
        return (
          <NavLink
            key={to}
            to={to}
            end={false}
            onClick={handleNav}
            className={baseCls + (active ? activeCls : inactiveCls)}
            data-testid={`atlas-nav-${slug(label)}`}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
            <span className="flex-1">{label}</span>
            <ItemPill pending={pending} badge={badge} isActive={active} />
          </NavLink>
        );
      })}

      <div className="mt-4 mb-1 px-3 text-[10px] font-hand uppercase tracking-widest text-tb-navy/40">
        Tools
      </div>
      {NAV_TOOLS.map((item) => {
        const { to, label, icon: Icon, pending } = item;
        const active = isActive(item);
        return (
          <NavLink
            key={to}
            to={to}
            onClick={handleNav}
            className={baseCls + (active ? activeCls : inactiveCls)}
            data-testid={`atlas-nav-${slug(label)}`}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
            <span className="flex-1">{label}</span>
            <ItemPill pending={pending} isActive={active} />
          </NavLink>
        );
      })}

      {isAdmin && (
        <>
          <div className="mt-4 mb-1 px-3 text-[10px] font-hand uppercase tracking-widest text-tb-navy/40">
            Admin
          </div>
          <NavLink
            to="/atlas/admin/advisors"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-advisors"
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={2} />
            <span>Advisors</span>
          </NavLink>
          <NavLink
            to="/atlas/admin/review-queue"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-review-queue"
          >
            <ClipboardCheck className="w-4 h-4" strokeWidth={2} />
            <span>Review queue</span>
          </NavLink>
          {/* Msg 588 · Phase 2 — Simplified Folder Presets admin. */}
          <NavLink
            to="/atlas/admin/presets"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-presets"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2} />
            <span>Folder Presets</span>
          </NavLink>
          {/* Msg 590 · Phase A — user-defined Categories admin. */}
          <NavLink
            to="/atlas/admin/categories"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-categories"
          >
            <Palette className="w-4 h-4" strokeWidth={2} />
            <span>Categories</span>
          </NavLink>
          {/* Msg 604 · Phase B — Global Attributes Library admin. */}
          <NavLink
            to="/atlas/admin/attributes"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-attributes"
          >
            <Tag className="w-4 h-4" strokeWidth={2} />
            <span>Attributes</span>
          </NavLink>
          {/* (2026-08-04) Bulk-add items — batch-create sibling folders
              pre-tagged with a chosen category. Saved presets targeting
              that category auto-fire per new topic. */}
          <NavLink
            to="/atlas/admin/bulk-add"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-bulk-add"
          >
            <FolderPlus className="w-4 h-4" strokeWidth={2} />
            <span>Bulk add items</span>
          </NavLink>
          {/* K2 (2026-08-05) — Knowledge-graph Ingestion (upload docs,
              extract entities/facts, admin-review, apply). */}
          <NavLink
            to="/atlas/admin/ingest"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-admin-ingest"
          >
            <Upload className="w-4 h-4" strokeWidth={2} />
            <span>Ingestion</span>
          </NavLink>
          {/* Msg 575 — Templates + Bulk attributes admin surfaces
              moved into the Topics tab (segmented control at the top
              of `/atlas/topics`). Sidebar entries retired. */}
          <NavLink
            to="/atlas/bulk-import"
            onClick={handleNav}
            className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
            data-testid="atlas-nav-bulk-import"
          >
            <PencilRuler className="w-4 h-4" strokeWidth={2} />
            <span>Bulk import</span>
          </NavLink>
        </>
      )}

      <div className="mt-4 mb-1 px-3 text-[10px] font-hand uppercase tracking-widest text-tb-navy/40">
        Help
      </div>
      <NavLink
        to="/atlas/support"
        onClick={handleNav}
        className={({ isActive: a }) => baseCls + (a ? activeCls : inactiveCls)}
        data-testid="atlas-nav-support"
      >
        <LifeBuoy className="w-4 h-4" strokeWidth={2} />
        <span className="flex-1">Support</span>
        <ItemPill pending isActive={false} />
      </NavLink>
    </nav>
  );
}
